import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, A11y, Autoplay, Scrollbar, Grid } from "swiper/modules";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Category } from "../../types/home-page";
import Modal from "../modals/adahy-modal";
import { useState } from "react";
import { cn } from "../../lib/utils";

export default function CategoriesSection({
    categories,
    ksa_national_day,
    oman_national_day,
    uae_national_day,
}: {
    categories: Category[];
    ksa_national_day: boolean;
    oman_national_day: boolean;
    uae_national_day: boolean;
}) {
    const { i18n, t } = useTranslation();
    const [showPopup, setShowPopup] = useState(false);

    const handleCategoryClick = (e: React.MouseEvent, categoryName: string) => {
        // Logic handled here if needed
    };

    return (
        <div className="mx-10 mb-10 lg:-mt-12 xl:mx-24 xl:-mt-64 xl:mb-20">
            <h3 className="mb-4 text-2xl font-medium text-[rgba(23,23,23,1)] xl:mb-8 xl:text-[28px]">
                {t("categories")}
            </h3>

            <Swiper
                // 3. Added Grid to modules
                modules={[Navigation, A11y, Autoplay, Scrollbar, Grid]}
                // 4. Grid Configuration
                grid={{
                    rows: 2,
                    fill: "row",
                }}
                slidesPerGroup={2}
                spaceBetween={24}
                breakpoints={{
                    0: {
                        slidesPerView: 2,
                        slidesPerGroup: 2,
                        grid: { rows: 2, fill: "row" },
                    },
                    640: {
                        slidesPerView: 4,
                        slidesPerGroup: 2,
                        grid: { rows: 2, fill: "row" },
                    },
                    1024: {
                        slidesPerView: 6,
                        slidesPerGroup: 2,
                        grid: { rows: 2, fill: "row" },
                    },
                    1536: {
                        slidesPerView: 7.2,
                        slidesPerGroup: 2,
                        grid: { rows: 2, fill: "row" },
                    },
                }}
                scrollbar={{ enabled: true, draggable: true, dragSize: 30 }}
                navigation={{
                    enabled: true,
                    nextEl: ".swiper-button-next",
                    prevEl: ".swiper-button-prev",
                }}
                autoplay={{ pauseOnMouseEnter: true, delay: 3000 }}
                dir={i18n.dir()}
                key={i18n.language + "categories-grid"}
                className="!pb-16 !pt-4 lg:!pt-0"
            >
                {/* Custom Navigation Buttons */}
                <div className="swiper-button-next !size-10 !rounded-full !bg-white/90 shadow-md after:!text-lg hover:!bg-white">
                    {" "}
                    <svg
                        width="12"
                        height="22"
                        viewBox="0 0 12 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="!h-[22px] !w-[12px]"
                    >
                        <path
                            d="M1.125 0.828125L0.1875 1.71875C0 1.95312 0 2.32812 0.1875 2.51562L8.67188 11L0.1875 19.5312C0 19.7188 0 20.0938 0.1875 20.3281L1.125 21.2188C1.35938 21.4531 1.6875 21.4531 1.92188 21.2188L11.7656 11.4219C11.9531 11.1875 11.9531 10.8594 11.7656 10.625L1.92188 0.828125C1.6875 0.59375 1.35938 0.59375 1.125 0.828125Z"
                            fill="#171717"
                        />
                    </svg>
                </div>
                <div className="swiper-button-prev !size-10 !rounded-full !bg-white/90 shadow-md after:!text-lg hover:!bg-white">
                    <svg
                        width="12"
                        height="22"
                        viewBox="0 0 12 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                        className="!h-[22px] !w-[12px]"
                    >
                        <path
                            d="M10.875 21.1719L11.8125 20.2812C12 20.0469 12 19.6719 11.8125 19.4844L3.32812 11L11.8125 2.46875C12 2.28125 12 1.90625 11.8125 1.67188L10.875 0.78125C10.6406 0.546875 10.3125 0.546875 10.0781 0.78125L0.234375 10.5781C0.046875 10.8125 0.046875 11.1406 0.234375 11.375L10.0781 21.1719C10.3125 21.4062 10.6406 21.4062 10.875 21.1719Z"
                            fill="#171717"
                        />
                    </svg>
                </div>

                {categories.map((category) =>
                    // dont show adahi category and market

                    category.id === 2 || category.is_market === 1 ? null : (
                        <SwiperSlide
                            key={category.id}
                            className="mb-2 mt-2 !h-auto"
                        >
                            <Link
                                to={`/category/${category.id}`}
                                onClick={(e) =>
                                    handleCategoryClick(e, category.name)
                                }
                                className="group block h-full"
                            >
                                <div
                                    className={cn(
                                        "flex h-full flex-col items-center justify-center rounded-2xl p-4 transition-all duration-300",
                                        "border border-gray-100 bg-white shadow-sm hover:-translate-y-1 hover:shadow-lg",
                                        ksa_national_day &&
                                            "hover:border-[#21e86d]",
                                        oman_national_day &&
                                            "hover:border-[#EF3B3B]",
                                        uae_national_day &&
                                            "hover:border-[#FD1A12]",
                                    )}
                                >
                                    <div
                                        className={cn(
                                            "relative mb-3 flex aspect-square w-full items-center justify-center overflow-hidden rounded-xl bg-gray-50",
                                            uae_national_day &&
                                                "border-b-2 border-[#FD1A12]",
                                            oman_national_day &&
                                                "border-b-2 border-[#EF3B3B]",
                                            ksa_national_day &&
                                                "border-b-2 border-[#21e86d]",
                                        )}
                                    >
                                        <img
                                            src={category.image}
                                            alt={category.name}
                                            className="object-fit h-full w-full transition-transform duration-500 group-hover:scale-110"
                                        />
                                    </div>

                                    <span className="line-clamp-2 text-center text-xs font-bold uppercase tracking-tight text-gray-800 group-hover:text-primary xl:text-sm">
                                        {category.name}
                                    </span>
                                </div>
                            </Link>
                        </SwiperSlide>
                    ),
                )}
            </Swiper>

            {showPopup && <Modal onClose={() => setShowPopup(false)} />}
        </div>
    );
}
